import { useMemo, useRef, type ReactNode } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from 'recharts';
import {
  Antenna,
  Hammer,
  Plane,
  Radio,
  Rocket,
  Shield,
  Ship,
  Sparkles,
} from 'lucide-react';
import { StarlinkFootprintMap } from '@/components/value-chain/explorer/StarlinkFootprintMap';
import { useStarlinkDashboard } from '@/components/value-chain/explorer/StarlinkIndependentSeries';
import { useStarlinkMarkets } from '@/components/value-chain/explorer/StarlinkDemandMap';
import { bucketById } from '@/components/value-chain/starlinkDashboard';
import {
  KIT_COST_INDEX,
  LAUNCH_SERIES,
  OVERVIEW_SECTIONS,
  bucketStat,
  formatUpdated,
  parseKitRows,
  qosCurve,
  sparkFromHistory,
  type OverviewSectionId,
} from '@/components/value-chain/starlinkOverview';

type Props = {
  onBack: () => void;
  onOpenFinancials: () => void;
  onOpenValueChain: () => void;
  onOpenIndependent: () => void;
};

const PIE = [
  { name: 'Starlink (est.)', value: 75, color: '#38bdf8' },
  { name: 'Kuiper (Amazon)', value: 8, color: '#818cf8' },
  { name: 'OneWeb', value: 6, color: '#22d3ee' },
  { name: 'Other LEO', value: 6, color: '#34d399' },
  { name: 'GEO (legacy)', value: 5, color: '#64748b' },
];

const GOV_CHIPS = ['Defense', 'Disaster response', 'Border security', 'International aid', 'Other federal agencies'];
const SPEC_CHIPS = ['MSS approval', 'Direct-to-cell (D2C)', 'Key Areas', 'Global spectrum rights', 'Regulatory filings'];

export function StarlinkGlobalOverview({
  onBack,
  onOpenFinancials,
  onOpenValueChain,
  onOpenIndependent,
}: Props) {
  const dashboard = useStarlinkDashboard();
  const markets = useStarlinkMarkets();
  const scroller = useRef<HTMLDivElement>(null);
  const constellation = bucketById(dashboard, 'constellation');
  const kits = parseKitRows(bucketById(dashboard, 'kits'));
  const qos = bucketById(dashboard, 'qos');
  const live = markets.kpis.live;
  const printedMarkets = dashboard.printed_markets;
  const operational = bucketStat(dashboard, 'constellation', 'McDowell operational orbit');
  const gp = bucketStat(dashboard, 'constellation', 'CelesTrak GP objects');
  const printedSats = bucketStat(dashboard, 'constellation', '10-Q operational print');
  const orbitLaunch = bucketStat(dashboard, 'constellation', 'McDowell in orbit / launched').split('/');
  const inOrbit = orbitLaunch[0]?.trim() ?? '—';
  const launched = orbitLaunch[1]?.trim() ?? '—';
  const fyLaunches = bucketStat(dashboard, 'launch', 'Launches FY 2025');
  const q1Falcon = bucketStat(dashboard, 'launch', 'Falcon launches Q1 2026');
  const mcdowellLaunched = bucketStat(dashboard, 'launch', 'McDowell sats launched (cum.)');
  const govHits = bucketStat(dashboard, 'government', 'USAspending Starlink hits');
  const govDollars = bucketStat(dashboard, 'government', 'Shown award dollars');
  const medianDl = bucketStat(dashboard, 'qos', 'Median download p50 (measured markets)');
  const medianLat = bucketStat(dashboard, 'qos', 'Median latency p50');
  const ookla = qos?.rows.find((row) => row.label.includes('Ookla US'))?.value ?? '22.5%';
  const speedCurve = useMemo(() => qosCurve(markets.markets).map((mbps, i) => ({ i, mbps })), [markets.markets]);
  const consumerSpark = sparkFromHistory('consumerM');
  const egSpark = sparkFromHistory('enterpriseM');
  const subSpark = sparkFromHistory('subscribersM');

  const go = (id: OverviewSectionId) => {
    if (id === 'financials') {
      onOpenFinancials();
      return;
    }
    const node = scroller.current?.querySelector(`#sl-${id}`);
    node?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#071422] text-slate-100">
      <header className="flex flex-wrap items-center gap-3 border-b border-white/10 px-5 py-3">
        <button type="button" onClick={onBack} className="flex items-center gap-2 text-left">
          <span className="flex h-8 w-8 items-center justify-center text-white" aria-hidden>
            <svg viewBox="0 0 64 64" className="h-7 w-7">
              <path d="M6 8h18L58 56H40L6 8Z" fill="currentColor" />
              <path d="M40 8h18L32 36h-12L40 8Z" fill="currentColor" />
            </svg>
          </span>
          <span>
            <span className="block text-[18px] leading-none font-semibold tracking-tight">Starlink</span>
            <span className="mt-0.5 block text-[11px] text-slate-400">Global Overview</span>
          </span>
        </button>
        <p className="hidden text-[11px] text-slate-500 lg:block">Satellite network. Global connectivity. A more connected world.</p>
        <nav className="flex min-w-0 flex-1 flex-wrap items-center justify-center gap-1">
          {OVERVIEW_SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => go(section.id)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
                section.id === 'overview' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              {section.label}
            </button>
          ))}
        </nav>
        <div className="ml-auto text-right text-[10px] text-slate-500">
          <p>Last updated</p>
          <p className="text-slate-300">{formatUpdated(dashboard.as_of)}</p>
          <p className="mt-0.5 rounded bg-white/10 px-1.5 py-0.5 text-slate-200">TTM (Jun 2026)</p>
        </div>
      </header>

      <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <div id="sl-overview" className="mb-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-6">
          <KpiCard
            label="Satellites (operational)"
            value={operational}
            delta="McDowell orbit"
            spark={subSpark.map((n) => n * 800)}
          />
          <KpiCard label="Markets (live)" value={String(live)} delta="+ vs 10-Q 167" spark={[160, 164, 167, live]} />
          <KpiCard label="Subscribers (EoP)" value="12.0M" delta="+101.2% YoY" spark={subSpark} />
          <KpiCard label="Consumer Revenue (Q2)" value="$2.485B" delta="+44% YoY" spark={consumerSpark} />
          <KpiCard label="E&G Revenue (Q2)" value="$1.806B" delta="+108% YoY" spark={egSpark} />
          <KpiCard label="Median Download Speed" value={medianDl.replace(' Mbps', '') + ' Mbps'} spark={speedCurve.map((row) => row.mbps)} />
        </div>

        <div className="mb-3 grid gap-3 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.25fr)_minmax(0,1.15fr)]">
          <Panel id="sl-constellation" title="Constellation Census">
            <div className="grid grid-cols-3 gap-2">
              <Metric value={printedSats} label="SpaceX 10-Q satellites" />
              <Metric value={gp} label="CelesTrak GP objects" />
              <Metric value={operational} label="McDowell operational" />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Metric value={inOrbit} label="in orbit (McDowell)" />
              <Metric value={launched} label="launched (McDowell)" />
            </div>
            <p className="mt-3 text-[10px] leading-relaxed text-slate-500">{constellation?.note}</p>
          </Panel>

          <Panel
            id="sl-demand"
            title="Global Footprint"
            action={
              <span className="flex flex-wrap items-center gap-3 text-[10px] text-slate-400">
                <Legend swatch="#34d399" label={`Live (${live})`} />
                <Legend swatch="#60a5fa" label="Authorized / pending" />
                <Legend swatch="#64748b" label="Not available" />
              </span>
            }
          >
            <div className="grid h-[210px] grid-cols-[1fr_118px] gap-3">
              <StarlinkFootprintMap markets={markets.markets} />
              <div className="flex flex-col justify-center gap-4">
                <div>
                  <p className="text-[28px] leading-none font-semibold text-emerald-400">{live}</p>
                  <p className="mt-1 text-[11px] text-slate-400">live markets (availability.json)</p>
                </div>
                <div>
                  <p className="text-[28px] leading-none font-semibold text-white">{printedMarkets}</p>
                  <p className="mt-1 text-[11px] text-slate-400">markets in 10-Q</p>
                </div>
                <button type="button" onClick={onOpenIndependent} className="text-left text-[11px] text-sky-400 hover:text-sky-300">
                  View market list →
                </button>
              </div>
            </div>
          </Panel>

          <Panel id="sl-launches" title="Launch Cadence & Capacity" icon={<Rocket className="h-3.5 w-3.5 text-sky-300" />}>
            <div className="mb-2 grid grid-cols-5 gap-1.5">
              <TinyStat value={fyLaunches} label="launches in FY25" />
              <TinyStat value={q1Falcon} label="Falcon launches in Q1" />
              <TinyStat value={mcdowellLaunched} label="total launched (McDowell)" />
              <TinyStat value="1 Tbps" label="V3 capacity per sat" />
              <TinyStat value="60" label="V3 sats per Starship" />
            </div>
            <div className="flex h-[150px] gap-3">
              <div className="min-w-0 flex-1">
                <p className="mb-1 text-[10px] text-slate-500">Starlink Launches</p>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={LAUNCH_SERIES} barSize={14}>
                    <XAxis dataKey="year" tick={{ fill: '#94a3b8', fontSize: 9 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={darkTip} formatter={(value) => [String(value), 'Launches']} />
                    <Bar dataKey="launches" radius={[3, 3, 0, 0]}>
                      {LAUNCH_SERIES.map((row) => (
                        <Cell key={row.year} fill={row.printed ? '#38bdf8' : '#1d4ed8'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="w-[132px] text-[10px] leading-relaxed text-slate-400">
                Higher capacity V3 satellites and Starship (60 sats/launch) enable faster network buildout. FY25 170 and Q1 Falcon 40 are printed; earlier bars are public Falcon cadence.
              </p>
            </div>
          </Panel>
        </div>

        <div className="mb-3 grid gap-3 lg:grid-cols-2 xl:grid-cols-4">
          <Panel
            id="sl-geography"
            title="Kit Geography (Exports)"
            action={<LinkBtn onClick={onOpenIndependent}>View trade data →</LinkBtn>}
          >
            <table className="w-full text-left text-[12px]">
              <thead className="text-[10px] tracking-wide text-slate-500 uppercase">
                <tr>
                  <th className="pb-1.5 font-medium">Country</th>
                  <th className="pb-1.5 font-medium">Units (labeled BOLs)</th>
                  <th className="pb-1.5 font-medium">Shipments (lines)</th>
                </tr>
              </thead>
              <tbody>
                {kits.map((row) => (
                  <tr key={row.country} className="border-t border-white/5">
                    <td className="py-1.5 text-slate-200">{row.country}</td>
                    <td className="py-1.5">{row.units?.toLocaleString() ?? 'N/A'}</td>
                    <td className="py-1.5">{row.lines?.toLocaleString() ?? 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-[10px] text-slate-500">Source: Labeled BOLs. HS 851762 captures satellite terminals (not Starlink-only).</p>
          </Panel>

          <Panel
            id="sl-government"
            title="Government Contracts"
            icon={<Shield className="h-3.5 w-3.5 text-sky-300" />}
            action={<LinkBtn onClick={onOpenIndependent}>View contracts →</LinkBtn>}
          >
            <div className="grid grid-cols-3 gap-2">
              <Metric value={govHits} label="Starlink hits (USAspending)" />
              <Metric value={govDollars} label="shown value" />
              <Metric value="$1,806M" label="Enterprise & Gov revenue (Q2)" />
            </div>
            <p className="mt-3 mb-1.5 text-[10px] text-slate-500">Notable Areas</p>
            <div className="flex flex-wrap gap-1.5">
              {GOV_CHIPS.map((chip) => (
                <span key={chip} className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[10px] text-slate-300">
                  {chip}
                </span>
              ))}
            </div>
          </Panel>

          <Panel
            id="sl-spectrum"
            title="Spectrum & Regulatory"
            icon={<Radio className="h-3.5 w-3.5 text-sky-300" />}
            action={<LinkBtn onClick={onOpenIndependent}>View regulatory timeline →</LinkBtn>}
          >
            <div className="grid grid-cols-3 gap-2">
              <Metric value="DA-26-471" label="EchoStar MSS approval" />
              <Metric value="~650" label="D2C satellites (planned/authorized)" />
              <Metric value="~30" label="countries (D2C)" />
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {SPEC_CHIPS.map((chip) => (
                <span key={chip} className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[10px] text-slate-300">
                  {chip}
                </span>
              ))}
            </div>
          </Panel>

          <Panel
            id="sl-performance"
            title="Network Performance"
            icon={<Antenna className="h-3.5 w-3.5 text-sky-300" />}
            action={<LinkBtn onClick={onOpenIndependent}>View performance details →</LinkBtn>}
          >
            <div className="mb-1 flex items-end justify-between gap-2">
              <div>
                <p className="text-[22px] leading-none font-semibold">{medianDl}</p>
                <p className="mt-1 text-[10px] text-slate-500">median download</p>
                <p className="mt-2 text-[16px] font-semibold">{medianLat}</p>
                <p className="text-[10px] text-slate-500">median latency</p>
              </div>
              <div className="h-[88px] w-[55%] min-w-0">
                <p className="mb-0.5 text-right text-[9px] text-slate-500">Median Download Speed (measured markets)</p>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={speedCurve}>
                    <Area type="monotone" dataKey="mbps" stroke="#38bdf8" fill="#0ea5e9" fillOpacity={0.25} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <p className="text-[11px] text-slate-400">
              Ookla (U.S. sample) <span className="font-semibold text-white">{ookla}</span> of tests from Starlink
            </p>
          </Panel>
        </div>

        <div className="mb-3 grid gap-3 lg:grid-cols-3">
          <Panel
            title="High-ARPU Verticals"
            icon={<span className="flex gap-1 text-sky-300"><Ship className="h-3.5 w-3.5" /><Plane className="h-3.5 w-3.5" /></span>}
            action={<LinkBtn onClick={onOpenIndependent}>View verticals →</LinkBtn>}
          >
            <div className="grid grid-cols-3 gap-2">
              <Metric value=">150k" label="vessels (company report)" />
              <Metric value=">1,400" label="aircraft (company report)" />
              <Metric value=">1,000" label="business jets" />
            </div>
            <p className="mt-3 text-[10px] text-slate-500">Key verticals: Maritime, Aviation, Land Mobility, Government, Fixed Sites.</p>
          </Panel>

          <Panel
            title="BOM / Kit Cost"
            icon={<Hammer className="h-3.5 w-3.5 text-sky-300" />}
            action={<LinkBtn onClick={onOpenIndependent}>View cost analysis →</LinkBtn>}
          >
            <div className="flex gap-3">
              <div>
                <p className="text-[28px] leading-none font-semibold text-emerald-400">−59%</p>
                <p className="mt-1 text-[11px] text-slate-400">kit cost vs 2022 (company data)</p>
              </div>
              <div className="h-[92px] min-w-0 flex-1">
                <p className="text-[9px] text-slate-500">Estimated Kit Cost Index</p>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={KIT_COST_INDEX}>
                    <XAxis dataKey="year" tick={{ fill: '#64748b', fontSize: 9 }} axisLine={false} tickLine={false} />
                    <Area type="monotone" dataKey="index" stroke="#38bdf8" fill="#0ea5e9" fillOpacity={0.2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <p className="mt-2 text-[10px] text-slate-500">Additional context: BLS PPI, Census HS4 (key still needed for Census).</p>
          </Panel>

          <Panel
            id="sl-competition"
            title="Competition Landscape"
            action={<LinkBtn onClick={onOpenIndependent}>View competitive analysis →</LinkBtn>}
          >
            <div className="flex items-center gap-3">
              <div className="relative h-[110px] w-[110px] shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={PIE} dataKey="value" innerRadius={32} outerRadius={50} stroke="none">
                      {PIE.map((slice) => (
                        <Cell key={slice.name} fill={slice.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-[13px] font-semibold">~75%</p>
              </div>
              <ul className="min-w-0 flex-1 space-y-1 text-[11px] text-slate-300">
                {PIE.map((slice) => (
                  <li key={slice.name} className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full" style={{ background: slice.color }} />
                      {slice.name}
                    </span>
                    <span className="text-slate-500">{slice.name.startsWith('Starlink') ? '~75%' : slice.name === 'OneWeb' ? '648 sats' : ''}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="mt-2 text-[10px] text-slate-500">Other Context · BEAD locations ~476k vs ~417k (build vs serve). Starlink share is S-1; other slices visualize the remainder.</p>
          </Panel>
        </div>

        <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-[#0c1c31] px-4 py-3">
          <div className="flex min-w-0 items-start gap-2">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-sky-300" />
            <div>
              <p className="text-[12px] font-semibold">Key Takeaway</p>
              <p className="mt-0.5 max-w-4xl text-[12px] leading-relaxed text-slate-400">
                Starlink continues to rapidly expand its constellation and global footprint, with strong subscriber growth, improving performance, and increasing capacity from V3 and Starship. Significant runway remains across consumer, enterprise, and government markets.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onOpenValueChain}
            className="shrink-0 rounded-lg bg-sky-500 px-3 py-2 text-[12px] font-medium text-slate-950 hover:bg-sky-400"
          >
            Explore the full Starlink value chain →
          </button>
        </section>
      </div>
    </div>
  );
}

function Panel({
  id,
  title,
  icon,
  action,
  children,
}: {
  id?: string;
  title: string;
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section id={id} className="rounded-2xl border border-white/10 bg-[#0c1c31] p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-[13px] font-semibold text-white">
          {icon}
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function KpiCard({
  label,
  value,
  delta,
  spark,
}: {
  label: string;
  value: string;
  delta?: string;
  spark?: number[];
}) {
  const data = (spark ?? []).map((v, i) => ({ i, v }));
  return (
    <article className="rounded-2xl border border-white/10 bg-[#0c1c31] px-3 py-2.5">
      <p className="text-[10px] text-slate-400">{label}</p>
      <div className="mt-1 flex items-end justify-between gap-2">
        <p className="text-[22px] leading-none font-semibold tracking-tight">{value}</p>
        {data.length > 1 ? (
          <div className="h-8 w-16">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <Area type="monotone" dataKey="v" stroke="#34d399" fill="#34d399" fillOpacity={0.2} strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : null}
      </div>
      {delta ? <p className="mt-1 text-[10px] font-medium text-emerald-400">{delta}</p> : null}
    </article>
  );
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <p className="text-[20px] leading-none font-semibold tracking-tight">{value}</p>
      <p className="mt-1 text-[10px] leading-snug text-slate-500">{label}</p>
    </div>
  );
}

function TinyStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-lg bg-white/5 px-1.5 py-1.5">
      <p className="text-[15px] leading-none font-semibold">{value}</p>
      <p className="mt-1 text-[9px] leading-tight text-slate-500">{label}</p>
    </div>
  );
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="h-2 w-2 rounded-full" style={{ background: swatch }} />
      {label}
    </span>
  );
}

function LinkBtn({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="text-[11px] text-sky-400 hover:text-sky-300">
      {children}
    </button>
  );
}

const darkTip = {
  fontSize: 11,
  borderRadius: 8,
  border: '1px solid #1e3a5f',
  background: '#0b1728',
  color: '#e2e8f0',
};
